/* ============================ FLEET PAGES: overview, sites, node, workloads, search ============================ */
import { Fragment, useContext, useEffect, useState, type MouseEvent, type ReactNode } from "react";
import {
  DB, act, api, can, ctrById, fire, go, job, node, nodeOf, rerender, site, siteNameOf, stackById, toggle, ui, wl,
} from "../lib/store";
import { ago, gb, lvl, since, statusClass } from "../lib/fmt";
import { Badge, Bars, Chips, Sparkline, showMenu } from "../ui/common";
import { Btn, F, FormCtx, Modal, useForm } from "../ui/modal";
import { TermBox } from "../ui/term";
import { wCounts } from "../App";
import { PAGES, type PageProps } from "./registry";
import { JobTable } from "./ops";
import { MonitoringPanel } from "./workload";
import { clusterOf, haOn, joinCluster, leaveCluster, migrate, newCluster } from "./clusters";
import { addWorkload, deployStack, importExisting, installSoftware } from "../modals/deploy";
import { addSite, claim, deleteItem, drift, editSite, logs, runPlaybook, shell, snapshot, versions } from "../modals/ops";

const healthCls = (h: number) => (h < 50 ? "bad" : h < 85 ? "warn" : "");

/* ---------- Overview ---------- */
function Home() {
  const c = wCounts();
  const dayAgo = Date.now() - 86400e3;
  const attention: { sev: string; t: string; d: string; go: () => void }[] = [
    ...DB.unclaimed.map((u: any) => ({ sev: "acc", t: `${u.name} is waiting to be claimed`, d: `${u.ip} · checked in ${ago(u.last_seen)}`, go: () => claim() })),
    ...DB.nodes.filter((n: any) => n.status !== "online").map((n: any) => ({
      sev: n.status === "offline" ? "bad" : "warn", t: `${n.name} is ${n.status}`,
      d: `${site(n.site).name} · ${n.status_reason || "last seen " + n.lastSeen}`, go: () => go("node", n.id),
    })),
    ...DB.nodes.filter((n: any) => n.disk >= 85).map((n: any) => ({
      sev: "warn", t: `${n.name} disk at ${n.disk}%`, d: "Free space or add storage before the next backup", go: () => go("node", n.id),
    })),
    ...DB.containers.filter((x: any) => x.restarts > 3 || x.status === "restarting" || x.health === "unhealthy").map((x: any) => ({
      sev: "warn", t: `${x.name} is ${x.health === "unhealthy" ? "unhealthy" : "restarting"} (${x.restarts}×)`,
      d: `on ${wl(x.workload)?.name || "?"} · check container logs`, go: () => logs(x.id),
    })),
    ...DB.workloads.filter((w: any) => w.drift.length).map((w: any) => ({
      sev: "warn", t: `${w.name} has drifted from its saved state`,
      d: `${w.drift.length} change${w.drift.length > 1 ? "s" : ""} made directly in Proxmox`, go: () => drift(w.id),
    })),
    ...DB.jobs.filter((j: any) => j.status === "failed" && new Date(j.finished || j.created).getTime() > dayAgo).map((j: any) => ({
      sev: "bad", t: `Job ${j.id} failed`, d: j.action, go: () => go("job", j.id),
    })),
    ...DB.backups.filter((b: any) => b.status === "failed" || b.status === "stale").map((b: any) => ({
      sev: "warn", t: `Backup of ${b.target} is ${b.status}`, d: `last ${b.last ? ago(b.last) : "never"} · ${b.schedule}`, go: () => go("backups"),
    })),
  ];
  const offline = DB.nodes.filter((n: any) => n.status === "offline");
  const alerts = DB.alerts.filter((a: any) => !a.ack && !a.resolved);
  const k8s = DB.workloads.filter((w: any) => w.k8s).length;
  return (
    <>
      <div className="pagehead">
        <div><h2>Fleet overview</h2><p>{DB.sites.length} site{DB.sites.length === 1 ? "" : "s"}, {c.nodes} edge device{c.nodes === 1 ? "" : "s"}. Everything below is live from Proxmox and the agents.</p></div>
        <div className="spacer" />
        <div className="btnrow">
          {can("Admin") ? <button className="btn" onClick={() => claim()}>Claim a device</button> : null}
          {can("Admin") ? <button className="btn primary" onClick={() => addWorkload()}>+ New workload</button> : null}
        </div>
      </div>
      {offline.map((n: any) => (
        <div key={n.id} className="banner"><span>△</span>
          <div><b>{n.name} has not checked in for {since(n.last_seen)}</b>Queued jobs for this device will run automatically when it reconnects. Site: {site(n.site).name}.</div>
          <div className="spacer" /><button className="btn sm" onClick={() => go("node", n.id)}>Open device</button>
        </div>
      ))}
      {!c.nodes ? (
        <div className="banner acc"><span>◈</span>
          <div><b>No edge devices yet</b>Install the agent on a Proxmox host (it prints a claim code) or connect one manually with an API token.</div>
          <div className="spacer" />{can("Admin") ? <button className="btn sm primary" onClick={() => claim()}>Add a device</button> : null}
        </div>
      ) : null}
      <div className="grid g4">
        <div className="kpi" onClick={() => go("sites")}><div className="n">{c.online}/{c.nodes}</div><div className="l">Edge devices online</div><div className="sub">{c.offline} offline · {c.degraded} degraded</div></div>
        <div className="kpi" onClick={() => go("workloads")}><div className="n">{c.vms + c.lxc}</div><div className="l">VMs and LXC containers</div><div className="sub">{c.vms} VMs · {c.lxc} LXC</div></div>
        <div className="kpi" onClick={() => go("workloads")}><div className="n">{c.ctr}</div><div className="l">Docker containers</div><div className="sub">{DB.stacks.length} stacks · {k8s} k3s cluster{k8s === 1 ? "" : "s"}</div></div>
        <div className="kpi" onClick={() => go("alerts")}><div className="n">{c.alerts}</div><div className="l">Open alerts</div><div className="sub">{alerts.filter((a: any) => a.sev === "critical").length} critical · {alerts.filter((a: any) => a.sev === "warning").length} warning</div></div>
      </div>

      <div className="grid g2" style={{ marginTop: 16 }}>
        <div className="card"><div className="hd"><h3>Needs attention</h3><div className="spacer" /><button className="btn sm ghost" onClick={() => go("alerts")}>All alerts</button></div>
          <div className="bd" style={{ padding: 0 }}>
            {attention.length ? attention.map((a, i) => (
              <div key={i} className="row" style={{ display: "flex", gap: 11, padding: "12px 16px", borderBottom: "1px solid var(--line-2)", cursor: "pointer" }} onClick={() => a.go()}>
                <span className={"badge " + a.sev} style={{ alignSelf: "flex-start" }}><span className="dot" /></span>
                <div><b style={{ fontWeight: 500 }}>{a.t}</b><div style={{ color: "var(--ink-2)", fontSize: 12.5 }}>{a.d}</div></div>
              </div>
            )) : <div className="empty" style={{ padding: 30 }}><h3>All clear</h3><p>Nothing needs you right now.</p></div>}
          </div></div>
        <div className="card"><div className="hd"><h3>Sites</h3><div className="spacer" /><button className="btn sm ghost" onClick={() => go("sites")}>Manage</button></div>
          <div className="bd" style={{ padding: 0 }}>
            {DB.sites.length ? DB.sites.map((s: any) => {
              const ns = DB.nodes.filter((x: any) => String(x.site) === String(s.id));
              const n = ns[0];
              return (
                <div key={s.id} className="row" style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", borderBottom: "1px solid var(--line-2)", cursor: "pointer" }}
                  onClick={() => (n ? go("node", n.id) : go("sites"))}>
                  <div style={{ flex: 1 }}><b style={{ fontWeight: 500 }}>{s.name}</b>
                    <div style={{ fontSize: 12.5, color: "var(--ink-3)" }} className="mono">{n ? `${n.ip} · agent ${n.agent}${ns.length > 1 ? ` · +${ns.length - 1} more` : ""}` : "no device yet"}</div></div>
                  <div style={{ width: 96 }}><div className="metric"><span>Health</span><b>{s.health}</b></div><div className="bar"><i className={healthCls(s.health)} style={{ width: s.health + "%" }} /></div></div>
                  {n ? <Badge s={n.status} /> : <span className="badge">empty</span>}
                </div>
              );
            }) : <div className="empty" style={{ padding: 24 }}><p>No sites yet.</p></div>}
          </div></div>
      </div>

      <div className="card" style={{ marginTop: 16 }}><div className="hd"><h3>Recent jobs</h3><div className="spacer" /><button className="btn sm ghost" onClick={() => go("jobs")}>Open tasks</button></div>
        <div style={{ overflow: "auto" }}><table><thead><tr><th>Job</th><th>Action</th><th>Target</th><th>Started</th><th>Duration</th><th>Status</th></tr></thead><tbody>
          {DB.jobs.length ? DB.jobs.slice(0, 6).map((j: any) => (
            <tr key={j.id} className="row" style={{ cursor: "pointer" }} onClick={() => go("job", j.id)}>
              <td className="mono">{j.id}</td><td>{j.action}</td><td>{j.target}</td><td>{j.startedTxt}</td><td className="mono">{j.dur}</td><td><Badge s={j.status} /></td>
            </tr>
          )) : <tr><td colSpan={6} className="muted">No jobs yet.</td></tr>}
        </tbody></table></div></div>
    </>
  );
}
PAGES.home = { crumb: () => "Fleet overview", C: Home };

/* ---------- Sites & nodes ---------- */
function Sites() {
  return (
    <>
      <div className="pagehead"><div><h2>Sites and edge devices</h2>
        <Chips items={[[DB.sites.length, DB.sites.length === 1 ? "site" : "sites"], [DB.nodes.length, DB.nodes.length === 1 ? "device" : "devices"],
          [DB.nodes.filter((n: any) => n.status === "online").length, "online", "ok"],
          DB.nodes.some((n: any) => n.status === "offline") && [DB.nodes.filter((n: any) => n.status === "offline").length, "offline", "bad"],
          DB.nodes.some((n: any) => n.status === "degraded") && [DB.nodes.filter((n: any) => n.status === "degraded").length, "degraded", "warn"]]} />
        <p>Each device dials out to this console. You never open a port on the customer's network.</p></div><div className="spacer" />
        <div className="btnrow">{can("Admin") ? <><button className="btn" onClick={() => claim()}>Claim a device</button><button className="btn" onClick={() => addSite()}>Add site</button></> : null}</div></div>
      {DB.unclaimed.length ? (
        <div className="banner acc"><span>◈</span>
          <div><b>{DB.unclaimed.length} device{DB.unclaimed.length > 1 ? "s" : ""} waiting to be assigned</b>{DB.unclaimed.map((u: any) => u.name + " (" + u.ip + ")").join(", ")} — enter the claim code shown on the device to finish setup.</div>
          <div className="spacer" /><button className="btn sm primary" onClick={() => claim()}>Assign</button></div>
      ) : null}
      <div className="tbl-wrap"><table><thead><tr><th style={{ width: "26%" }}>Device</th><th>Site</th><th>Status</th><th>Management IP</th><th>LAN IP</th><th style={{ width: 170 }}>Resources</th><th>Guests</th><th>Agent</th><th></th></tr></thead><tbody>
        {DB.nodes.length ? DB.nodes.map((n: any) => {
          const guests = DB.workloads.filter((w: any) => w.node === n.id).length;
          return (
            <tr key={n.id} className="row">
              <td><div className="tname"><span className="type-badge type-node">Edge node</span><div><a onClick={() => go("node", n.id)}>{n.name}</a><small>{String(n.model || "").split(" · ")[0]}{n.settings.maintenance ? " · maintenance" : ""}</small></div></div></td>
              <td>{site(n.site).name}</td>
              <td><span title={n.status_reason || ""}><Badge s={n.status} /></span><div style={{ fontSize: 11.5, color: "var(--ink-3)" }}>{n.lastSeen}</div>
                {n.status_reason ? <div style={{ fontSize: 11.5, color: "var(--warn)", maxWidth: 240 }}>{n.status_reason}</div> : null}</td>
              <td className="mono">{n.ip}</td><td className="mono">{n.lan}</td>
              <td><Bars w={n} /></td>
              <td>{guests}</td>
              <td className="mono">{n.has_agent ? n.agent : "none"}{n.has_agent && n.agent !== DB.meta.agent_version ? <>{" "}<span className="badge warn" title={`Latest is ${DB.meta.agent_version}`}>old</span></> : null}</td>
              <td style={{ textAlign: "right" }}><RowMenu kind="node" id={n.id} /></td>
            </tr>
          );
        }) : <tr><td colSpan={9}><div className="empty"><h3>No devices yet</h3><p>Claim one with its code, or connect it manually.</p></div></td></tr>}
      </tbody></table></div>
      <div className="section-title">Site health</div>
      <div className="grid g4">{DB.sites.map((s: any) => (
        <div key={s.id} className="card"><div className="bd">
          <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}><div style={{ flex: 1 }}><b>{s.name}</b><div style={{ color: "var(--ink-3)", fontSize: 12.5, marginBottom: 9 }}>{s.city || "—"} · {s.nodes} device{s.nodes === 1 ? "" : "s"}</div></div>
            {can("Admin") ? <button className="btn sm ghost" title="Edit site" onClick={() => editSite(s.id)}>✎</button> : null}</div>
          <div className="metric"><span style={{ flex: 1 }}>Health score</span><b>{s.health}</b></div><div className="bar" style={{ marginBottom: 8 }}><i className={healthCls(s.health)} style={{ width: s.health + "%" }} /></div>
          <div style={{ fontSize: 12, color: "var(--ink-2)" }}>Connectivity, headroom, backups, patch policy and open alerts combined. Window: {s.window}.</div></div></div>
      ))}</div>
    </>
  );
}
PAGES.sites = { crumb: () => "Sites & edge nodes", C: Sites };

/* ---------- Node detail ---------- */
function NodeOverview({ n }: { n: any }) {
  const [rrd, setRrd] = useState<{ d?: any; err?: string } | null>(null);
  useEffect(() => {
    let alive = true;
    api("GET", `/api/nodes/${n.id}/rrd?tf=day`).then((d) => { if (alive) setRrd({ d }); }, (e) => { if (alive) setRrd({ err: e.message }); });
    return () => { alive = false; };
  }, [n.id]);
  return (
    <>
      <div className="grid g2"><div className="card"><div className="hd"><h3>Device</h3></div><div className="bd">
        <dl className="kv"><dt>Hardware</dt><dd>{n.model}</dd><dt>Architecture</dt><dd>{n.arch || "—"} · {n.cores || "?"} cores · {Math.round(n.mem_total / 1024) || "?"} GB</dd>
          <dt>Management IP</dt><dd>{n.ip} ({/^100\./.test(n.ip) ? "overlay" : "direct"})</dd><dt>LAN IP</dt><dd>{n.lan}</dd>
          <dt>Proxmox node</dt><dd>{n.pve_node}</dd><dt>Cluster</dt><dd>{n.cluster}</dd><dt>Agent</dt><dd>{n.has_agent ? n.agent : "not installed"}</dd><dt>Uptime</dt><dd>{n.uptimeTxt}</dd>
          <dt>CPU temp</dt><dd>{n.temp ? n.temp + " °C" : "—"}</dd><dt>Last check-in</dt><dd>{n.lastSeen}</dd><dt>Proxmox API</dt><dd>{n.api_ok ? "reachable" : "not reachable"}</dd></dl></div></div>
        <div className="card"><div className="hd"><h3>Storage</h3></div><div className="bd">
          {(n.storage || []).length ? (n.storage || []).map((st: any) => (
            <div key={st.id} style={{ marginBottom: 14 }}><div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}><b style={{ fontWeight: 500 }}>{st.id}</b><span className="mono">{st.pct}%</span></div>
              <div style={{ fontSize: 12, color: "var(--ink-3)", marginBottom: 5 }}>{st.type} · {gb(st.used)} of {gb(st.total)} · {st.content}{st.active ? "" : " · inactive"}</div><div className="bar"><i className={lvl(st.pct)} style={{ width: st.pct + "%" }} /></div></div>
          )) : <p className="muted">No storage reported yet.</p>}
          <div className="btnrow">{can("Operator") ? <button className="btn sm" onClick={() => fire(job("POST", `/api/nodes/${n.id}/rescan`))}>Rescan storage</button> : null}</div></div></div></div>
      <div className="card" style={{ marginTop: 14 }}><div className="hd"><h3>Last 24 hours</h3><div className="spacer" /><span className="muted" style={{ fontSize: 12 }}>from Proxmox RRD</span></div><div className="bd">
        <div className="legend" style={{ marginBottom: 8 }}><span><i style={{ background: "var(--accent)" }} />CPU</span><span><i style={{ background: "var(--ok)" }} />Memory</span></div>
        <div id="n24">
          {!rrd ? <div className="chart-empty">loading…</div>
            : rrd.err != null ? <div className="chart-empty">{rrd.err}</div>
              : <><Sparkline data={rrd.d.cpu} color="#3B4CCA" /><Sparkline data={rrd.d.mem} color="#1F8A5B" /></>}
        </div></div></div>
    </>
  );
}

function NodeConsole({ n }: { n: any }) {
  const [k, setK] = useState(0);
  return (
    <div className="card"><div className="hd"><h3>Shell · {n.name}</h3><div className="spacer" /><span className="badge acc">session recorded</span>
      <button className="btn sm" onClick={() => setK((x) => x + 1)}>Reconnect</button></div><div className="bd">
      {can("Operator") ? <><TermBox key={k} target={`node:${n.id}`} /><div className="hint">Root shell over SSH through the management overlay. Everything typed and shown is recorded for the audit log.</div></>
        : <p>Operators and above can open a shell.</p>}</div></div>
  );
}

export const HEALTH_LABELS: Record<string, string> = { disk: "Root disk %", ram: "Memory %", cpu: "CPU %" };
export const globalHealth = (k: string): any => {
  const c = DB.settings.connectivity || {};
  const v = c["health_" + k];
  return v == null ? ({ disk: 85, ram: 92, cpu: 95 } as Record<string, number>)[k] : v;
};
/* per-device health thresholds; values live in the surrounding form as h_disk / h_ram / h_cpu */
export function HealthInputs({ n }: { n: any }) {
  const f = useContext(FormCtx);
  const st = n.settings || {};
  return (
    <>
      {["disk", "ram", "cpu"].map((k) => {
        const own = st["health_" + k];
        const g = globalHealth(k);
        const v = f ? f.d["h_" + k] : own;
        return (
          <label key={k} className="f"><span>{HEALTH_LABELS[k]}</span>
            <input type="number" min="0" max="100" value={v == null ? "" : v} placeholder={`global: ${g ? g + "%" : "off"}`} disabled={!can("Admin")}
              onChange={(e) => f?.set("h_" + k, e.target.value)} /></label>
        );
      })}
    </>
  );
}
async function saveNode(id: any, d: any) {
  await act("PUT", `/api/nodes/${id}`, {
    name: d.name, site: +d.site, auto_updates: !!d.auto_updates,
    delete_protection: !!d.delete_protection, maintenance: !!d.maintenance,
    ...Object.fromEntries(["disk", "ram", "cpu"].map((k) => { const v = String(d["h_" + k] ?? "").trim(); return ["health_" + k, v === "" ? -1 : +v]; })),
  }, "Device settings saved", d.name);
}

function NodeSettings({ n }: { n: any }) {
  const st = n.settings || {};
  const f = useForm({
    name: n.name, site: n.site != null ? String(n.site) : String(DB.sites[0]?.id ?? ""),
    auto_updates: !!st.auto_updates, delete_protection: !!st.delete_protection, maintenance: !!st.maintenance,
    h_disk: st.health_disk == null ? "" : st.health_disk, h_ram: st.health_ram == null ? "" : st.health_ram, h_cpu: st.health_cpu == null ? "" : st.health_cpu,
  });
  return (
    <FormCtx.Provider value={f}>
      <div className="card"><div className="bd">
        <F.Text k="name" label="Display name" />
        <F.Sel k="site" label="Site" opts={DB.sites.map((s: any) => [String(s.id), s.name] as [any, ReactNode])} />
        <F.Check k="auto_updates" title="Automatic updates" sub={`patch_os.yml runs weekly inside the site's maintenance window (${site(n.site).window || ""}).`} />
        <F.Check k="delete_protection" title="Delete protection" sub="Blocks destroy actions on this device and sets Proxmox protection on its guests until switched off." />
        <F.Check k="maintenance" title="Maintenance mode" sub="Holds scheduled jobs and silences alerts for this device." />
        <div className="section-title">Degraded when at or above</div>
        <div className="grid g3"><HealthInputs n={n} /></div>
        <p className="hint" style={{ marginTop: -6 }}>Leave empty to use the global setting (Settings → Device health). 0 turns that check off for this device. Alert rules are separate: they only send notifications.</p>
        <div className="btnrow">{can("Admin") ? <Btn primary onClick={() => saveNode(n.id, f.d)}>Save changes</Btn> : <span className="muted">Admins can change device settings.</span>}</div>
      </div></div>
    </FormCtx.Provider>
  );
}

const NODE_TABS = ["overview", "workloads", "monitoring", "tasks", "console", "settings"];
function NodePage({ param, tab }: PageProps) {
  const n = node(param);
  if (!n) return <div className="empty"><h3>That device is gone</h3><button className="btn" onClick={() => go("sites")}>Back to sites</button></div>;
  const t = tab || "overview";
  const guests = DB.workloads.filter((w: any) => w.node === n.id);
  let body: ReactNode = null;
  if (t === "overview") body = <NodeOverview n={n} />;
  else if (t === "workloads") {
    body = guests.length ? <WorkloadTable list={guests} withNodes={false} /> : (
      <div className="empty"><h3>No guests on this device yet</h3><p>Create a VM or LXC container, or import one that already exists.</p>
        {can("Admin") ? <button className="btn primary" onClick={() => addWorkload(n.id)}>+ New workload</button> : null}</div>
    );
  } else if (t === "monitoring") body = <MonitoringPanel kind="node" id={n.id} name={n.name} cpu={n.cpu} ram={n.ram} disk={n.disk} />;
  else if (t === "tasks") body = <JobTable list={DB.jobs.filter((j: any) => j.node === n.id || guests.some((w: any) => w.id === j.workload))} />;
  else if (t === "console") body = <NodeConsole n={n} />;
  else body = <NodeSettings n={n} />;
  const cl = clusterOf(n);
  return (
    <>
      <div className="pagehead"><div>
        <div style={{ fontSize: 12.5, color: "var(--ink-3)", marginBottom: 3 }}><a onClick={() => go("sites")} style={{ cursor: "pointer" }}>Sites</a> / {site(n.site).name}</div>
        <h2>{n.name} <span className={"badge " + statusClass(n.status)} style={{ verticalAlign: "middle", fontSize: 12 }}><span className="dot" />{n.status}</span></h2>
        <Chips items={[[guests.filter((w: any) => w.type === "vm").length, "VMs"], [guests.filter((w: any) => w.type === "lxc").length, "LXC"],
          [guests.filter((w: any) => w.status === "running").length, "running", "ok"],
          [DB.containers.filter((c: any) => guests.some((g: any) => g.id === c.workload)).length, "containers"]]} />
        {n.status_reason ? <div className="banner" style={{ margin: "8px 0 4px" }}><span>△</span><div><b>Why {n.status}</b>{n.status_reason}</div></div> : null}
        <p>{n.model} · {n.ip}{cl ? <> · cluster <a style={{ cursor: "pointer" }} onClick={() => go("clusters")}>{cl.name}</a></> : null}</p></div><div className="spacer" />
        <div className="btnrow">
          <button className="btn" onClick={() => go("node", n.id, "console")}>Open shell</button>
          {can("Admin") ? <button className="btn" onClick={() => Modal.confirm(`Reboot ${n.name}?`, "Every guest on this device restarts too (unless it is set not to start on boot).", "Reboot",
            () => fire(job("POST", `/api/nodes/${n.id}/reboot`)))}>Reboot</button> : null}
          {can("Admin") ? <button className="btn primary" onClick={() => addWorkload(n.id)}>+ New workload</button> : null}
        </div></div>
      <div className="tabs">{NODE_TABS.map((x) => (
        <button key={x} className={t === x ? "on" : ""} onClick={() => go("node", n.id, x)}>{x[0].toUpperCase() + x.slice(1)}</button>
      ))}</div>
      {body}
    </>
  );
}
PAGES.node = { crumb: (param) => node(param)?.name || "Not found", C: NodePage };

/* ---------- row action menu ---------- */
function menuItems(kind: string, id: any): ReactNode {
  const op = can("Operator"), ad = can("Admin");
  if (kind === "node") {
    const n = node(id); if (!n) return null;
    return <>
      <button onClick={() => go("node", n.id)}>Open device</button>
      <button onClick={() => go("node", n.id, "console")}>Open shell</button>
      {ad ? <button onClick={() => addWorkload(n.id)}>New VM or LXC…</button> : null}
      {op ? <button onClick={() => fire(job("POST", `/api/nodes/${n.id}/sync`))}>Sync inventory now</button> : null}
      {ad ? <button onClick={() => fire(job("POST", `/api/nodes/${n.id}/agent`))}>{n.has_agent ? "Upgrade" : "Install"} edge agent</button> : null}
      {ad ? <button onClick={() => fire(act("PUT", `/api/nodes/${n.id}`, { maintenance: !n.settings.maintenance }, n.settings.maintenance ? "Maintenance mode off" : "Maintenance mode on", `${n.name}`))}>
        {n.settings.maintenance ? "Leave" : "Enter"} maintenance mode</button> : null}
      {ad && !n.cluster_id && DB.clusters.length ? <button onClick={() => joinCluster(DB.clusters[0].id, n.id)}>Add to a cluster…</button> : null}
      {ad && !n.cluster_id && !DB.clusters.length ? <button onClick={() => newCluster()}>Create a cluster…</button> : null}
      {ad && n.cluster_id ? <button onClick={() => leaveCluster(n.id)}>Remove from cluster…</button> : null}
      {ad ? <><hr /><button className="danger" onClick={() => deleteItem("node", n.id)}>Remove device…</button></> : null}
    </>;
  }
  if (kind === "workload") {
    const w = wl(id); if (!w) return null;
    const run = w.status === "running";
    const inCluster = !!clusterOf(node(w.node));
    return <>
      <button onClick={() => go("workload", w.id)}>Open workload</button>
      <button onClick={() => go("workload", w.id, "console")}>Open console</button>
      {op && !w.external ? <button onClick={() => fire(job("POST", `/api/workloads/${w.id}/power`, { action: run ? "stop" : "start" }))}>{run ? "Stop" : "Start"}</button> : null}
      {op && run ? <button onClick={() => fire(job("POST", `/api/workloads/${w.id}/power`, { action: "reboot" }))}>Reboot</button> : null}
      {op && !w.external ? <button onClick={() => snapshot(w.id)}>Take snapshot…</button> : null}
      {op && !w.external && inCluster ? <button onClick={() => migrate(w.id)}>Migrate to another member…</button> : null}
      {ad && !w.external && inCluster ? <button onClick={() => fire(job("POST", `/api/workloads/${w.id}/ha`, { enabled: !haOn(w) }))}>{haOn(w) ? "Turn off" : "Turn on"} high availability</button> : null}
      <hr />
      {!w.ssh_ok && ad ? <button onClick={() => fire(job("POST", `/api/workloads/${w.id}/enable-management`))}>Enable management (SSH access)</button> : null}
      {op ? (w.docker ? <button onClick={() => deployStack(w.id, "compose")}>Deploy compose stack…</button>
        : <button onClick={() => installSoftware(w.id)}>Install Docker…</button>) : null}
      {op && w.k8s ? <button onClick={() => deployStack(w.id, "k8s")}>Apply Kubernetes manifest…</button> : null}
      {op ? <button onClick={() => runPlaybook(`Workload: ${w.name}`)}>Run playbook…</button> : null}
      {ad ? <><hr /><button className="danger" onClick={() => deleteItem("workload", w.id)}>Delete {String(w.type).toUpperCase()}…</button></> : null}
    </>;
  }
  if (kind === "stack") {
    const s = stackById(id); if (!s) return null;
    return <>
      {op ? <button onClick={() => deployStack(s.workload, s.kind, s.id)}>Edit and redeploy…</button> : null}
      {op ? <button onClick={() => fire(job("POST", `/api/stacks/${s.id}/restart`, {}))}>Restart</button> : null}
      {op ? <button onClick={() => fire(job("POST", `/api/stacks/${s.id}/pull`, {}))}>Pull latest images</button> : null}
      <button onClick={() => versions(s.id)}>Version history…</button>
      {ad ? <><hr /><button className="danger" onClick={() => deleteItem("stack", s.id)}>Delete stack…</button></> : null}
    </>;
  }
  if (kind === "container") {
    const c = ctrById(id); if (!c) return null;
    return <>
      <button onClick={() => logs(c.id)}>View logs</button>
      {op ? <button onClick={() => shell(`container:${c.id}`, `${c.name}`)}>Open shell</button> : null}
      {op ? <button onClick={() => fire(job("POST", `/api/containers/${c.id}/restart`, {}))}>Restart</button> : null}
      {op ? <button onClick={() => fire(job("POST", `/api/containers/${c.id}/scan`, {}))}>Scan image for CVEs</button> : null}
      {ad ? <><hr /><button className="danger" onClick={() => deleteItem("container", c.id)}>Delete container…</button></> : null}
    </>;
  }
  return null;
}
export function RowMenu({ kind, id }: { kind: "node" | "workload" | "stack" | "container"; id: any }) {
  return <button className="btn sm ghost" onClick={(e: MouseEvent) => showMenu(e, menuItems(kind, id))}>⋯</button>;
}

/* ---------- workload table (hierarchical) ---------- */
export function WorkloadTable({ list, withNodes = true }: { list: any[]; withNodes?: boolean }) {
  const rows: ReactNode[] = [];
  const wRow = (w: any, cls: string) => {
    const kids = DB.stacks.filter((s: any) => s.workload === w.id).length + DB.containers.filter((c: any) => c.workload === w.id && !c.stack).length;
    const open = ui.expanded.has("w" + w.id);
    return (
      <tr key={"w" + w.id} className={"row " + cls}>
        <td><div className="tname">
          <span className="twist" onClick={() => toggle("w" + w.id)}>{kids ? (open ? "▾" : "▸") : ""}</span>
          <span className={"type-badge type-" + w.type}>{w.external ? "HOST" : String(w.type).toUpperCase()}</span>
          <div><a onClick={() => go("workload", w.id)}>{w.name}</a><small>{w.host} · {w.external ? "imported" : "vmid " + w.vmid}</small></div>
          {w.drift.length ? <span className="badge warn" title="Changed outside Edge Compute">drift</span> : null}
          {!w.ssh_ok && w.status === "running" ? <span className="badge" title="The console cannot SSH into this guest yet">no access</span> : null}
        </div></td>
        <td><Badge s={w.status} /></td>
        <td>{siteNameOf(w)}<div style={{ fontSize: 11.5, color: "var(--ink-3)" }}>{nodeOf(w).name}</div></td>
        <td className="mono">{w.ip}{w.mgmt_ip ? <div style={{ fontSize: 11, color: "var(--ink-3)" }}>{w.mgmt_ip}</div> : null}</td>
        <td style={{ width: 170 }}><Bars w={w} /></td>
        <td>{w.os}</td>
        <td>{(w.tags || []).map((t: any, i: number) => <Fragment key={i}>{i ? " " : ""}<span className="badge">{t}</span></Fragment>)}
          <div style={{ fontSize: 11.5, color: "var(--ink-3)", marginTop: 2 }}>{w.owner}</div></td>
        <td style={{ textAlign: "right" }}><RowMenu kind="workload" id={w.id} /></td>
      </tr>
    );
  };
  const stackRow = (s: any) => {
    const open = ui.expanded.has("s" + s.id);
    return (
      <tr key={"s" + s.id} className="row gchild"><td><div className="tname"><span className="twist" onClick={() => toggle("s" + s.id)}>{open ? "▾" : "▸"}</span>
        <span className={"type-badge type-" + (s.kind === "k8s" ? "k8s" : "docker")}>{s.kind === "k8s" ? "K8s" : "Compose"}</span>
        <div><a onClick={() => deployStack(s.workload, s.kind, s.id)}>{s.name}</a><small>{s.services} services · {s.version ? "v" + s.version : "found on host"} · {s.updatedTxt}</small></div></div></td>
        <td><Badge s={s.status} /></td>
        <td colSpan={4} style={{ color: "var(--ink-3)", fontSize: 12.5 }}>deployed on {wl(s.workload)?.name || ""}</td>
        <td></td><td style={{ textAlign: "right" }}><RowMenu kind="stack" id={s.id} /></td></tr>
    );
  };
  const ctrRow = (c: any) => (
    <tr key={"c" + c.id} className="row gchild"><td style={{ paddingLeft: 86 }}><div className="tname">
      <span className="type-badge type-docker">Container</span>
      <div><a onClick={() => logs(c.id)}>{c.name}</a><small>{c.image}</small></div>
      {c.restarts > 3 ? <span className="badge bad">{c.restarts} restarts</span> : null}</div></td>
      <td><Badge s={c.status} /></td>
      <td colSpan={2} className="mono" style={{ fontSize: 12 }}>{c.ports}</td>
      <td style={{ width: 170 }}><Bars w={{ cpu: c.cpu, ram: c.ram, disk: 0 }} /></td>
      <td colSpan={2} style={{ fontSize: 12.5, color: "var(--ink-2)" }}>health: {c.health}</td>
      <td style={{ textAlign: "right" }}><RowMenu kind="container" id={c.id} /></td></tr>
  );
  const pushChildren = (w: any) => {
    if (!ui.expanded.has("w" + w.id)) return;
    DB.stacks.filter((s: any) => s.workload === w.id).forEach((s: any) => {
      rows.push(stackRow(s));
      if (ui.expanded.has("s" + s.id)) DB.containers.filter((c: any) => c.stack === s.id).forEach((c: any) => rows.push(ctrRow(c)));
    });
    DB.containers.filter((c: any) => c.workload === w.id && !c.stack).forEach((c: any) => rows.push(ctrRow(c)));
  };

  if (withNodes) {
    DB.nodes.forEach((n: any) => {
      const guests = list.filter((w: any) => w.node === n.id);
      if (!guests.length && (ui.filter.type || ui.filter.status)) return;
      if (ui.filter.site && String(n.site) !== String(ui.filter.site)) return;
      const open = ui.expanded.has("n" + n.id);
      rows.push(
        <tr key={"n" + n.id} className="row"><td><div className="tname"><span className="twist" onClick={() => toggle("n" + n.id)}>{open ? "▾" : "▸"}</span>
          <span className="type-badge type-node">Edge node</span><div><a onClick={() => go("node", n.id)}>{n.name}</a><small>{site(n.site).name}</small></div></div></td>
          <td><Badge s={n.status} /></td>
          <td>{site(n.site).name}</td><td className="mono">{n.ip}</td><td style={{ width: 170 }}><Bars w={n} /></td>
          <td>{String(n.model || "").split(" · ")[0]}</td><td>{guests.length} guests</td><td style={{ textAlign: "right" }}><RowMenu kind="node" id={n.id} /></td></tr>,
      );
      if (open) guests.forEach((w: any) => { rows.push(wRow(w, "child")); pushChildren(w); });
    });
    list.filter((w: any) => !w.node).forEach((w: any) => { rows.push(wRow(w, "")); pushChildren(w); });
  } else {
    list.forEach((w: any) => { rows.push(wRow(w, "")); pushChildren(w); });
  }
  return (
    <div className="tbl-wrap"><table><thead><tr><th style={{ width: "30%" }}>Name</th><th>Status</th><th>Site / device</th><th>IP address</th><th>CPU · memory · disk</th><th>OS / image</th><th>Tags</th><th></th></tr></thead>
      <tbody>{rows.length ? rows : <tr><td colSpan={8}><div className="empty"><h3>Nothing matches</h3><p>Clear the filters to see the whole fleet.</p></div></td></tr>}</tbody></table></div>
  );
}

/* ---------- Workloads ---------- */
function Workloads() {
  const f = ui.filter;
  let list = DB.workloads;
  if (f.type) list = list.filter((w: any) => w.type === f.type);
  if (f.status) list = list.filter((w: any) => w.status === f.status);
  if (f.site) list = list.filter((w: any) => String(nodeOf(w).site) === String(f.site));
  const drifted = DB.workloads.filter((w: any) => w.drift.length);
  const setF = (k: string, v: string) => { ui.filter[k] = v; rerender(); };
  return (
    <>
      <div className="pagehead"><div><h2>Workloads</h2>
        <Chips items={[[DB.workloads.filter((w: any) => w.type === "vm").length, "VMs"], [DB.workloads.filter((w: any) => w.type === "lxc").length, "LXC"],
          DB.workloads.some((w: any) => w.external) && [DB.workloads.filter((w: any) => w.external).length, "imported"], [DB.containers.length, "containers"],
          [DB.workloads.filter((w: any) => w.status === "running").length, "running", "ok"],
          DB.workloads.some((w: any) => w.status === "unknown") && [DB.workloads.filter((w: any) => w.status === "unknown").length, "unknown (device unreachable)", "warn"]]} />
        <p>Devices, their VMs and LXC containers, and everything running inside them. Expand a row to go deeper.</p></div><div className="spacer" />
        <div className="btnrow">{can("Admin") ? <><button className="btn" onClick={() => importExisting()}>Import existing machine</button><button className="btn primary" onClick={() => addWorkload()}>+ New workload</button></> : null}</div></div>
      {drifted.map((w: any) => (
        <div key={w.id} className="banner"><span>△</span>
          <div><b>{w.name} was changed outside Edge Compute</b>{w.drift.map((d: any) => `${d.setting}: ${d.saved} → ${d.actual}`).join(" · ")}. You can adopt these changes or restore the saved state.</div>
          <div className="spacer" /><button className="btn sm" onClick={() => drift(w.id)}>Review drift</button></div>
      ))}
      <div className="tbl-wrap" style={{ borderBottom: 0, borderRadius: "8px 8px 0 0" }}>
        <div className="toolbar">
          <select value={f.type || ""} onChange={(e) => setF("type", e.target.value)}><option value="">All types</option><option value="vm">VM</option><option value="lxc">LXC</option></select>
          <select value={f.status || ""} onChange={(e) => setF("status", e.target.value)}><option value="">Any status</option>
            {["running", "stopped", "provisioning", "unknown"].map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}</select>
          <select value={f.site == null ? "" : String(f.site)} onChange={(e) => setF("site", e.target.value)}><option value="">All sites</option>
            {DB.sites.map((s: any) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}</select>
          <button className="btn sm ghost" onClick={() => { ui.filter = {}; rerender(); }}>Clear</button>
          <div style={{ flex: 1 }} />
          <button className="btn sm" onClick={() => {
            ui.expanded = new Set<string>(DB.nodes.map((n: any) => "n" + n.id).concat(DB.workloads.map((w: any) => "w" + w.id), DB.stacks.map((s: any) => "s" + s.id)));
            rerender();
          }}>Expand all</button>
          <button className="btn sm" onClick={() => { ui.expanded = new Set<string>(); rerender(); }}>Collapse all</button>
        </div></div>
      <WorkloadTable list={list} />
    </>
  );
}
PAGES.workloads = { crumb: () => "Workloads", C: Workloads };

/* ---------- Search ---------- */
function Search({ param }: PageProps) {
  const q = param || "";
  const t = q.toLowerCase(), has = (v: any) => String(v ?? "").toLowerCase().includes(t);
  const nodes = DB.nodes.filter((n: any) => has(n.name) || has(n.ip) || has(n.lan));
  const ws = DB.workloads.filter((w: any) => has(w.name) || has(w.ip) || has(w.host) || has(w.vmid) || has(w.mgmt_ip) || (w.tags || []).some(has));
  const cs = DB.containers.filter((c: any) => has(c.name) || has(c.image));
  const js = DB.jobs.filter((j: any) => has(j.id) || has(j.action) || has(j.target));
  const sec = (title: string, items: ReactNode[]) => items.length
    ? <><div className="section-title">{title}</div><div className="card"><div className="bd" style={{ padding: 0 }}>{items}</div></div></> : null;
  const line = (key: string, t1: any, t2: any, fn: () => void) => (
    <div key={key} className="row" style={{ padding: "11px 16px", borderBottom: "1px solid var(--line-2)", cursor: "pointer" }} onClick={fn}>
      <b style={{ fontWeight: 500 }}>{t1}</b><div style={{ fontSize: 12.5, color: "var(--ink-2)" }}>{t2}</div></div>
  );
  const total = nodes.length + ws.length + cs.length + js.length;
  return (
    <>
      <div className="pagehead"><div><h2>Results for “{q}”</h2><p>{total} match{total === 1 ? "" : "es"} across devices, workloads, containers and jobs.</p></div></div>
      {sec("Edge devices", nodes.map((n: any) => line("n" + n.id, n.name, `${site(n.site).name} · ${n.ip}`, () => go("node", n.id))))}
      {sec("VMs and LXC", ws.map((w: any) => line("w" + w.id, w.name, `${String(w.type).toUpperCase()} · ${w.ip} · ${nodeOf(w).name}`, () => go("workload", w.id))))}
      {sec("Containers", cs.map((c: any) => line("c" + c.id, c.name, c.image, () => logs(c.id))))}
      {sec("Jobs", js.slice(0, 40).map((j: any) => line("j" + j.id, j.id + " · " + j.action, j.target, () => go("job", j.id))))}
      {total ? null : <div className="empty"><h3>Nothing found</h3><p>Try a device name, an IP address, a vmid, a tag or a job number.</p></div>}
    </>
  );
}
PAGES.search = { crumb: (param) => `Search · ${param ?? ""}`, C: Search };
